import { Injectable } from '@angular/core';

import { forkJoin, Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { MissionAcceptanceApiService } from '@api-services/mission-acceptance-api.service';
import { AntiMoneyLaunderingApiService } from '@common/api/services/anti-money-laundering-api.service';
import { AntiMoneyLaundering } from '@common/models/anti-money-laundering';
import { MissionAcceptanceStatus } from '@enums/mission-acceptance-status.enum';
import { ApeCode } from '@models/ape-code';
import { Company } from '@models/company';
import { MissionAcceptance } from '@models/mission-acceptance/mission-acceptance';
import { MissionAcceptanceAcceptation } from '@models/mission-acceptance/mission-acceptance-acceptation';
import { MissionAcceptanceInformations } from '@models/mission-acceptance/mission-acceptance-informations';
import { MissionAcceptanceFile } from '@models/mission-acceptance-file';
import { VatSystem } from '@models/vat-system';
import { FileSaverData, FileService } from '@services/file.service';
import { CreateOrUpdateMissionAcceptanceDialogService } from '@shared-dialogs/create-or-update-mission-acceptance-dialog/create-or-update-mission-acceptance-dialog.service';

import { FacadeBase } from './facade.base';

@Injectable({
  providedIn: 'root'
})
export class MissionAcceptanceFacade extends FacadeBase {
  constructor(
    private readonly missionAcceptanceApiService: MissionAcceptanceApiService,
    private readonly commonDataApiService: CommonDataApiService,
    private readonly createOrUpdateMissionAcceptanceDialogService: CreateOrUpdateMissionAcceptanceDialogService,
    private readonly fileService: FileService,
    private readonly antiMoneyLaunderingApiService: AntiMoneyLaunderingApiService
  ) {
    super();
  }

  getMissionAcceptances(): Observable<MissionAcceptance[]> {
    return this.missionAcceptanceApiService.getMissionAcceptances();
  }

  getAntiMoneyLaunderingConclusions(
    antiMoneyLaundering: AntiMoneyLaundering,
    companyId?: number
  ): Observable<AntiMoneyLaundering> {
    return this.antiMoneyLaunderingApiService.getAntiMoneyLaunderingConclusions(antiMoneyLaundering, companyId);
  }

  createMissionAcceptance(missionAcceptance: MissionAcceptance): Observable<MissionAcceptance> {
    return this.missionAcceptanceApiService.createMissionAcceptance(missionAcceptance, missionAcceptance.company?.id);
  }

  updateMissionAcceptance(missionAcceptance: MissionAcceptance, companyId?: number): Observable<MissionAcceptance> {
    return this.missionAcceptanceApiService.updateMissionAcceptance(missionAcceptance, companyId);
  }

  downloadMissionAcceptancePdf(missionAcceptanceId: number, companyId?: number): Observable<FileSaverData> {
    return this.missionAcceptanceApiService.downloadMissionAcceptancePdf(missionAcceptanceId, companyId).pipe(
      tap((fileSaverData: FileSaverData) => {
        this.fileService.save(fileSaverData);
        this.snackbar.openSuccess(`Le rapport a bien été téléchargé`);
      })
    );
  }

  updateMissionAcceptanceStatus(
    missionAcceptanceId: number,
    missionAcceptanceStatus: MissionAcceptanceStatus,
    companyId?: number
  ): Observable<MissionAcceptance> {
    return this.missionAcceptanceApiService.updateMissionAcceptanceStatus(
      missionAcceptanceId,
      missionAcceptanceStatus,
      companyId
    );
  }

  unlockMissionAcceptance(missionAcceptanceId: number, companyId?: number): Observable<MissionAcceptance> {
    return this.missionAcceptanceApiService.unlockMissionAcceptance(missionAcceptanceId, companyId);
  }

  getMissionAcceptance(missionAcceptanceId: number, companyId?: number): Observable<MissionAcceptance> {
    return this.missionAcceptanceApiService.getMissionAcceptance(missionAcceptanceId, companyId);
  }

  openCreateMissionAcceptanceDialog(company: Company): Observable<MissionAcceptance> {
    return this.generateNewMissionAcceptance(company).pipe(
      switchMap((missionAcceptance: MissionAcceptance) =>
        this.openCreateOrUpdateMissionAcceptanceDialog(missionAcceptance)
      )
    );
  }

  openUpdateMissionAcceptanceDialog(missionAcceptanceId: number, companyId?: number): Observable<MissionAcceptance> {
    return this.getMissionAcceptance(missionAcceptanceId, companyId).pipe(
      switchMap((missionAcceptance: MissionAcceptance) =>
        this.openCreateOrUpdateMissionAcceptanceDialog(missionAcceptance)
      )
    );
  }

  openCreateOrUpdateMissionAcceptanceDialog(missionAcceptance: MissionAcceptance): Observable<MissionAcceptance> {
    return this.createOrUpdateMissionAcceptanceDialogService
      .open({
        data: {
          missionAcceptance
        }
      })
      .afterClosed()
      .pipe(filter((createdOrUpdatedMissionAcceptance: MissionAcceptance) => !!createdOrUpdatedMissionAcceptance));
  }

  addMissionAcceptanceFile(
    missionAcceptance: MissionAcceptance,
    file: MissionAcceptanceFile,
    companyId?: number
  ): Observable<MissionAcceptanceFile> {
    return this.missionAcceptanceApiService.addMissionAcceptanceFile(missionAcceptance.id, file, companyId);
  }

  addContractsMissionAcceptanceFiles(
    missionAcceptance: MissionAcceptance,
    files: MissionAcceptanceFile[],
    companyId?: number
  ): Observable<MissionAcceptanceFile[]> {
    return this.missionAcceptanceApiService.addContractsMissionAcceptanceFiles(missionAcceptance.id, files, companyId);
  }

  downloadFile(missionAcceptanceId: number, fileId: number, companyId?: number): Observable<FileSaverData> {
    return this.missionAcceptanceApiService.downloadMissionAcceptanceFile(missionAcceptanceId, fileId, companyId).pipe(
      tap((fileSaverData: FileSaverData) => {
        this.fileService.save(fileSaverData);
      })
    );
  }

  openFileInNewTab(missionAcceptanceId: number, fileId: number, companyId?: number): Observable<Blob> {
    return this.missionAcceptanceApiService.downloadMissionAcceptanceFile(missionAcceptanceId, fileId, companyId).pipe(
      map((fileSaverData: FileSaverData) => fileSaverData.file),
      tap((file: Blob) => this.fileService.openInNewTab(file))
    );
  }

  deleteMissionAcceptanceFile(missionAcceptanceId: number, fileId: number, companyId?: number): Observable<void> {
    return this.missionAcceptanceApiService.deleteMissionAcceptanceFile(missionAcceptanceId, fileId, companyId);
  }

  private generateNewMissionAcceptance(company: Company): Observable<MissionAcceptance> {
    return forkJoin([this.commonDataApiService.getApeCodes(), this.commonDataApiService.getVatSystems()]).pipe(
      map((data: [ApeCode[], VatSystem[]]) => {
        const apeCodes: ApeCode[] = data[0];
        const vatSystems: VatSystem[] = data[1];
        const missionAcceptance = new MissionAcceptance();
        missionAcceptance.informations = new MissionAcceptanceInformations();
        missionAcceptance.acceptation = new MissionAcceptanceAcceptation();
        missionAcceptance.antiMoneyLaundering = new AntiMoneyLaundering();

        missionAcceptance.status = MissionAcceptanceStatus.todo;
        missionAcceptance.company = company;
        missionAcceptance.companyName = company.name;
        missionAcceptance.companyReference = company.reference;
        missionAcceptance.companyLegalForm = company.legalForm;
        missionAcceptance.companyApeCode = apeCodes.find(
          (apeCode: ApeCode) => apeCode.id === company.apeCode?.id
        )?.code;
        missionAcceptance.companyTaxRegime = company.taxRegime;
        missionAcceptance.companyVatSystem = vatSystems.find(
          (vatSystem: VatSystem) => vatSystem.id === company.vatSystem?.id
        )?.code;
        missionAcceptance.companyStreet = company.street;
        missionAcceptance.companyPostalCode = company.postalCode;
        missionAcceptance.companyCity = company.city;

        missionAcceptance.legalStructure = company.legalStructure;
        missionAcceptance.signatory = company.signatory;

        missionAcceptance.antiMoneyLaundering.ape = missionAcceptance.companyApeCode;

        return missionAcceptance;
      })
    );
  }
}
